import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, Address, PaymentMethod, RegisterData, LoginData, GuestData } from '../types/user'
import { faker } from '@faker-js/faker'

interface AuthStore {
  user: User | null
  isAuthenticated: boolean
  isGuest: boolean
  guestData: GuestData | null
  addresses: Address[]
  paymentMethods: PaymentMethod[]

  // Auth actions
  login: (data: LoginData) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => void
  setGuestData: (data: GuestData) => void
  clearGuestData: () => void

  // Address actions
  addAddress: (address: Omit<Address, 'id' | 'created_at'>) => void
  updateAddress: (id: string, address: Partial<Address>) => void
  deleteAddress: (id: string) => void
  setDefaultAddress: (id: string) => void

  // Payment method actions
  addPaymentMethod: (method: Omit<PaymentMethod, 'id'>) => void
  deletePaymentMethod: (id: string) => void
  setDefaultPaymentMethod: (id: string) => void
}

// Mock user for demo purposes
const createMockUser = (name: string, email: string, phone: string): User => ({
  id: faker.string.uuid(),
  name,
  email,
  phone,
  avatar: `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(name)}`,
  role: 'customer',
  is_verified: true,
  created_at: new Date().toISOString(),
})

// Mock addresses for demo
const createMockAddresses = (userId: string): Address[] => [
  {
    id: faker.string.uuid(),
    user_id: userId,
    label: 'Home',
    full_name: 'Demo User',
    phone: '+880 1712345678',
    address_line1: 'House 123, Road 5',
    address_line2: 'Block A',
    city: 'Dhaka',
    area: 'Gulshan',
    postal_code: '1212',
    is_default: true,
    created_at: new Date().toISOString(),
  },
  {
    id: faker.string.uuid(),
    user_id: userId,
    label: 'Office',
    full_name: 'Demo User',
    phone: '+880 1812345678',
    address_line1: 'Tower 5, Floor 10',
    address_line2: 'Tejgaon Industrial Area',
    city: 'Dhaka',
    area: 'Tejgaon',
    postal_code: '1208',
    is_default: false,
    created_at: new Date().toISOString(),
  },
]

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isGuest: false,
      guestData: null,
      addresses: [],
      paymentMethods: [],

      login: async (data: LoginData) => {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500))

        const user = createMockUser('Demo User', data.email_or_phone, '+880 1712345678')
        const addresses = createMockAddresses(user.id)

        set({
          user,
          isAuthenticated: true,
          isGuest: false,
          guestData: null,
          addresses,
          paymentMethods: [],
        })
      },

      register: async (data: RegisterData) => {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500))

        const user = createMockUser(data.name, data.email, data.phone)

        set({
          user,
          isAuthenticated: true,
          isGuest: false,
          guestData: null,
          addresses: [],
          paymentMethods: [],
        })
      },

      logout: () => {
        set({
          user: null,
          isAuthenticated: false,
          isGuest: false,
          guestData: null,
          addresses: [],
          paymentMethods: [],
        })
      },

      setGuestData: (data: GuestData) => {
        set({
          isGuest: true,
          guestData: data,
        })
      },

      clearGuestData: () => {
        set({
          isGuest: false,
          guestData: null,
        })
      },

      addAddress: (address) => {
        const newAddress: Address = {
          ...address,
          id: faker.string.uuid(),
          created_at: new Date().toISOString(),
        }

        const addresses = get().addresses
        // If this is the first address or marked as default, update others
        if (newAddress.is_default || addresses.length === 0) {
          set({
            addresses: [
              ...addresses.map(a => ({ ...a, is_default: false })),
              { ...newAddress, is_default: true },
            ],
          })
        } else {
          set({ addresses: [...addresses, newAddress] })
        }
      },

      updateAddress: (id, updates) => {
        set({
          addresses: get().addresses.map(a =>
            a.id === id ? { ...a, ...updates } : a
          ),
        })
      },

      deleteAddress: (id) => {
        const addresses = get().addresses.filter(a => a.id !== id)
        // If deleted address was default, make first remaining address default
        if (addresses.length > 0 && !addresses.some(a => a.is_default)) {
          addresses[0].is_default = true
        }
        set({ addresses })
      },

      setDefaultAddress: (id) => {
        set({
          addresses: get().addresses.map(a => ({
            ...a,
            is_default: a.id === id,
          })),
        })
      },

      addPaymentMethod: (method) => {
        const newMethod: PaymentMethod = {
          ...method,
          id: faker.string.uuid(),
        }

        const methods = get().paymentMethods
        if (newMethod.is_default || methods.length === 0) {
          set({
            paymentMethods: [
              ...methods.map(m => ({ ...m, is_default: false })),
              { ...newMethod, is_default: true },
            ],
          })
        } else {
          set({ paymentMethods: [...methods, newMethod] })
        }
      },

      deletePaymentMethod: (id) => {
        const methods = get().paymentMethods.filter(m => m.id !== id)
        if (methods.length > 0 && !methods.some(m => m.is_default)) {
          methods[0].is_default = true
        }
        set({ paymentMethods: methods })
      },

      setDefaultPaymentMethod: (id) => {
        set({
          paymentMethods: get().paymentMethods.map(m => ({
            ...m,
            is_default: m.id === id,
          })),
        })
      },
    }),
    {
      name: 'BotTola-auth',
    }
  )
)
